import { useEffect, useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Alert,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import { ADMIN_FIELD_LABELS } from '../constants/admin.constants'
import { dropdownRequestApi } from '../../dropdown/services/dropdownApi'
import type { DropdownRequest } from '../../dropdown/types/dropdown.types'
import type { ApiError, SnackbarState } from '../../../shared/types/common'

export function OptionRequestsTab() {
  const [requests, setRequests] = useState<DropdownRequest[]>([])
  const [statusFilter, setStatusFilter] = useState('pending')
  const [loading, setLoading] = useState(true)
  const [disciplinePrefixes, setDisciplinePrefixes] = useState<Record<number, string>>({})
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    void fetchRequests()
  }, [statusFilter])

  async function fetchRequests() {
    setLoading(true)
    try {
      const response = await dropdownRequestApi.list(statusFilter ? { status: statusFilter } : {})
      setRequests(response.data)
    } catch {
      showSnackbar('Failed to load option requests', 'error')
    } finally {
      setLoading(false)
    }
  }

  async function handleApprove(id: number) {
    const request = requests.find((item) => item.id === id)
    const prefix = request?.field_name === 'discipline' ? (disciplinePrefixes[id] || '').trim().toUpperCase() : undefined
    if (request?.field_name === 'discipline' && !prefix) {
      showSnackbar('Prefix is required to approve a discipline request', 'error')
      return
    }

    try {
      await dropdownRequestApi.approve(id, { prefix })
      showSnackbar('Request approved and option created')
      setDisciplinePrefixes((current) => {
        const next = { ...current }
        delete next[id]
        return next
      })
      await fetchRequests()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to approve request', 'error')
    }
  }

  async function handleReject(id: number) {
    try {
      await dropdownRequestApi.reject(id)
      showSnackbar('Request rejected')
      await fetchRequests()
    } catch (error) {
      showSnackbar((error as ApiError).response?.data?.message || 'Failed to reject request', 'error')
    }
  }

  function showSnackbar(message: string, severity: SnackbarState['severity'] = 'success') {
    setSnackbar({ open: true, message, severity })
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6">Option Requests</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={event => setStatusFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Requested Value</TableCell>
                <TableCell>Internal Value</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Justification</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No option requests found
                  </TableCell>
                </TableRow>
              ) : (
                requests.map(request => (
                  <TableRow key={request.id}>
                    <TableCell>{ADMIN_FIELD_LABELS[request.field_name] || request.field_name}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{request.requested_label}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{request.suggested_internal_value}</TableCell>
                    <TableCell>{request.requested_by_name}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>{request.justification || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={request.status}
                        color={request.status === 'approved' ? 'success' : request.status === 'rejected' ? 'error' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {request.status === 'pending' && (
                        <>
                          {request.field_name === 'discipline' && (
                            <TextField
                              size="small"
                              label="Prefix"
                              value={disciplinePrefixes[request.id] || ''}
                              onChange={(event) =>
                                setDisciplinePrefixes((current) => ({
                                  ...current,
                                  [request.id]: event.target.value.toUpperCase(),
                                }))
                              }
                              inputProps={{ maxLength: 6 }}
                              sx={{ mr: 1, width: 92 }}
                            />
                          )}
                          <Tooltip title="Approve">
                            <IconButton size="small" sx={{ color: '#198754' }} onClick={() => handleApprove(request.id)}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small" sx={{ color: '#b42318' }} onClick={() => handleReject(request.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(current => ({ ...current, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}
